import { call, put, takeLatest } from "redux-saga/effects";
import { ActionWithPayload } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";

import { EVAL_RUN_SUBMIT_GENERIC_ERROR } from "../constants";
import {
  EvalDatasetSummary,
  EvalRunDetail,
  EvalRunListResponse,
  EvalRunsFilter,
  EvalRunSubmitError,
  SubmitEvalRunPayload,
  SubmitEvalRunResponse,
} from "../interfaces";
import { clearEvalRun, deleteEvalRun, getEvalDatasets, getEvalRun, getEvalRuns, submitEvalRun } from "./actions";
import api from "../api";

export function* getEvalRunsSaga({ payload }: ActionWithPayload<EvalRunsFilter>) {
  try {
    yield put(startLoading());
    const response: EvalRunListResponse = yield call(api.getEvalRuns, payload);
    yield put(stopLoading());
    yield put(getEvalRuns.success(response));
  } catch (error) {
    yield put(getEvalRuns.failure(error as Error));
    yield put(stopLoading());
  }
}

/**
 * Registered (below) as a takeLatest target for BOTH `getEvalRun.request` and `clearEvalRun`, so
 * navigating away from a run's detail cancels an in-flight poll read rather than letting it land
 * afterward. `clearEvalRun` carries no payload — copying `Procedure/store/saga.tsx`'s
 * `getSemanticProceduresSaga` guard exactly: without this, the clear action's own dispatch would
 * invoke this worker with an undefined payload, fetch `/admin/evals/runs/undefined`, and dispatch a
 * FAILURE into the store — populating an error on the very action that exists to clear one.
 */
export function* getEvalRunSaga(action: ActionWithPayload<string> | ReturnType<typeof clearEvalRun>) {
  if (action.type === clearEvalRun().type) return;
  const { payload } = action as ActionWithPayload<string>;

  try {
    yield put(startLoading());
    const response: EvalRunDetail = yield call(api.getEvalRun, payload);
    yield put(stopLoading());
    yield put(getEvalRun.success(response));
  } catch (error) {
    yield put(getEvalRun.failure(error as Error));
    yield put(stopLoading());
  }
}

/**
 * What the shared `request` client's response interceptor actually rejects with when a request
 * reaches the API and the API answers with an error — `{ code, message }`
 * (`api/app/middlewares/errorHandler.ts`'s wire shape; see `request.tsx`'s `Promise.reject(data)`
 * branch). A narrowing type predicate, not a cast: `request.tsx` also rejects the raw `AxiosError`
 * when there is no response body at all (a network failure, a CORS failure, an aborted refresh), and
 * an `AxiosError.code` is a STRING (e.g. "ERR_NETWORK") — a `code: number` cast would silently accept
 * that value instead of catching it.
 */
const isApiErrorBody = (error: unknown): error is { code: number; message: string } =>
  typeof (error as { code?: unknown })?.code === "number" &&
  typeof (error as { message?: unknown })?.message === "string";

const toSubmitError = (error: unknown): EvalRunSubmitError =>
  isApiErrorBody(error)
    ? { code: error.code, message: error.message, isConflict: error.code === 409 }
    : { code: null, message: EVAL_RUN_SUBMIT_GENERIC_ERROR, isConflict: false };

export function* submitEvalRunSaga({ payload }: ActionWithPayload<SubmitEvalRunPayload>) {
  try {
    yield put(startLoading());
    const response: SubmitEvalRunResponse = yield call(api.submitEvalRun, payload);
    yield put(stopLoading());
    yield put(submitEvalRun.success(response));
  } catch (error) {
    yield put(submitEvalRun.failure(toSubmitError(error)));
    yield put(stopLoading());
  }
}

export function* deleteEvalRunSaga({ payload }: ActionWithPayload<string>) {
  try {
    yield put(startLoading());
    // DELETE /admin/evals/runs/:uuid returns 204 No Content — no body to read the uuid back from, so
    // the success payload is built from the request's own payload (the uuid we already know), exactly
    // as Chat's deleteChatSaga does for its own 204 delete.
    yield call(api.deleteEvalRun, payload);
    yield put(stopLoading());
    yield put(deleteEvalRun.success({ uuid: payload }));
  } catch (error) {
    yield put(deleteEvalRun.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getEvalDatasetsSaga() {
  try {
    yield put(startLoading());
    const response: EvalDatasetSummary[] = yield call(api.getEvalDatasets);
    yield put(stopLoading());
    yield put(getEvalDatasets.success(response));
  } catch (error) {
    yield put(getEvalDatasets.failure(error as Error));
    yield put(stopLoading());
  }
}

function* evaluationSagas() {
  yield takeLatest(getEvalRuns.request, getEvalRunsSaga);
  yield takeLatest([getEvalRun.request, clearEvalRun], getEvalRunSaga);
  yield takeLatest(submitEvalRun.request, submitEvalRunSaga);
  yield takeLatest(deleteEvalRun.request, deleteEvalRunSaga);
  yield takeLatest(getEvalDatasets.request, getEvalDatasetsSaga);
}

export default evaluationSagas;
